import { useCallback, useEffect } from "react";
import {
  formatQuarterHhMm,
  parseQuarterClock,
  QUARTER_CLOCK_HOUR_OPTIONS,
  QUARTER_CLOCK_MINUTE_OPTIONS,
  type QuarterClockMinute,
} from "@/shared/lib/quarter-time";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";

export type QuarterHourMinuteSelectProps = {
  /** "HH:mm" */
  value: string;
  onChange: (next: string) => void;
  className?: string;
  disabled?: boolean;
};

/** ຊມ + ນທ (00 / 15 / 30 / 45) — ເລືອກຈາກລາຍການ ແທນ native time picker */
export function QuarterHourMinuteSelect({
  value,
  onChange,
  className,
  disabled,
}: QuarterHourMinuteSelectProps) {
  const rawValue = value ?? "";

  useEffect(() => {
    const trimmed = rawValue.trim();
    if (!trimmed) return;
    const { h, m } = parseQuarterClock(trimmed);
    const canon = formatQuarterHhMm(h, m);
    if (trimmed !== canon) {
      onChange(canon);
    }
  }, [rawValue, onChange]);

  const { h, m } = parseQuarterClock(rawValue || "09:00");

  const commit = useCallback(
    (nh: number, nm: QuarterClockMinute) => {
      onChange(formatQuarterHhMm(nh, nm));
    },
    [onChange],
  );

  return (
    <div
      className={["flex min-w-0 gap-1.5", className].filter(Boolean).join(" ")}
    >
      <SimpleSelect
        value={String(h)}
        onValueChange={(v) => !disabled && commit(Number(v), m)}
        options={QUARTER_CLOCK_HOUR_OPTIONS}
        className="h-10 min-h-10 min-w-0 flex-1 text-base sm:text-sm"
        disabled={disabled}
      />
      <SimpleSelect
        value={String(m)}
        onValueChange={(v) =>
          !disabled && commit(h, Number(v) as QuarterClockMinute)
        }
        options={QUARTER_CLOCK_MINUTE_OPTIONS}
        className="h-10 min-h-10 w-24 shrink-0 text-base sm:text-sm"
        disabled={disabled}
      />
    </div>
  );
}
